import React, {
  DetailedHTMLProps,
  FocusEvent,
  forwardRef,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { InfoCircle } from '../../assets/icons/index';
import { Company } from '../../hooks/index';
import { useForwardRef } from '../../hooks/use-forward-ref.hook';
import {
  InputStatus,
  inputStatusToPlaceholderClassNamesMap,
  inputStatusToWrapperClassNamesMap,
} from './input.const';

export type InputProps = {
  currencySign?: boolean;
  customStatus?: InputStatus;
  disabled?: boolean;
  disabledInput?: boolean;
  errorMessage?: false | ReactNode | string;
  icon?: ReactNode;
  iconBeforeInput?: ReactNode;
  iconBeforeInputClassName?: string;
  inputClassName?: string;
  intelNumber?: boolean;
  isError?: boolean;
  isShownErrorMessage?: boolean;
  isSuccess?: boolean;
  maxDecimalPlaces?: number;
  numberOnly?: true;
  onChange: (value: string) => void;
  onIconBeforeInputClick?: () => void;
  onIconClick?: () => void;
  placeholder?: ReactNode;
  placeholderClassName?: string;
  regexp?: (value: string) => string;
  shouldDisplayZero?: boolean;
  shouldFormatNumber?: boolean;
  signAfterValue?: string;
  type?: HTMLInputTypeAttribute;
  value: string;
  wrapperClassName?: string;
} & Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'inputMode' | 'onChange' | 'placeholder'
>;

const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(
    (
      {
        value,
        onChange,
        placeholder,
        type,
        isError,
        errorMessage,
        isSuccess,
        disabled,
        icon,
        wrapperClassName,
        onBlur,
        onFocus,
        onIconClick,
        onIconBeforeInputClick,
        iconBeforeInput,
        customStatus,
        disabledInput,
        isShownErrorMessage = true,
        numberOnly,
        maxDecimalPlaces,
        placeholderClassName,
        shouldFormatNumber = false,
        inputClassName,
        iconBeforeInputClassName,
        className,
        currencySign,
        signAfterValue,
        shouldDisplayZero,
        intelNumber = true,
        regexp,
        ...props
      },
      ref,
    ) => {
      const stringValue = String(value);

      const { selectedCompany: { currency } = {} } = Company.useSelected();
      const formatNumber = useCallback(
        (value: string): string => {
          const rawValue = value.replace(/,/g, '');
          if (!rawValue) return rawValue;

          const [integerPart = '', decimalPart = ''] = rawValue.split('.');
          const hasDecimal = rawValue.includes('.');
          const hasTrailingDot = rawValue.endsWith('.');

          const normalizedInteger = integerPart.replace(/\D/g, '');
          const formattedInteger = normalizedInteger
            ? new Intl.NumberFormat('en-US').format(Number(normalizedInteger))
            : '';

          if (!hasDecimal) return formattedInteger;

          const slicedDecimal =
            maxDecimalPlaces !== undefined ? decimalPart.slice(0, maxDecimalPlaces) : decimalPart;
          const decimalSuffix = hasTrailingDot ? '' : slicedDecimal;

          return `${formattedInteger}.${decimalSuffix}`;
        },
        [maxDecimalPlaces],
      );

      const inputRef = useForwardRef<HTMLInputElement>(ref);
      const [isFocused, setIsFocused] = useState(false);
      const [status, setStatus] = useState(InputStatus.IDLE_EMPTY);
      const [inputValue, setInputValue] = useState(value);
      const selectionRef = useRef<{ end: number; start: number } | null>(null);

      const mapCaretPosition = useCallback(
        (rawValue: string, formattedValue: string, caret: number) => {
          if (caret <= 0) return 0;
          const targetCount = rawValue.slice(0, caret).replace(/,/g, '').length;
          if (targetCount <= 0) return 0;

          let count = 0;
          for (let i = 0; i < formattedValue.length; i += 1) {
            if (formattedValue[i] !== ',') {
              count += 1;
            }
            if (count === targetCount) {
              return i + 1;
            }
          }

          return formattedValue.length;
        },
        [],
      );

      useEffect(() => {
        if (isError || errorMessage) {
          setStatus(InputStatus.ERROR);
          return;
        }

        if (isSuccess) {
          setStatus(InputStatus.SUCCESS);
          return;
        }

        if (isFocused) {
          setStatus(InputStatus.FOCUSED);
          return;
        }

        setStatus(value ? InputStatus.IDLE_FULL : InputStatus.IDLE_EMPTY);
      }, [errorMessage, inputRef, isError, isFocused, isSuccess, value]);

      useEffect(() => {
        if (isFocused && shouldFormatNumber) return;
        if (shouldFormatNumber || (numberOnly && intelNumber) || numberOnly) {
          if (!shouldDisplayZero && stringValue === '0') {
            setInputValue('');
          } else {
            setInputValue(intelNumber ? formatNumber(stringValue) : stringValue);
          }
        } else {
          setInputValue(stringValue);
        }
      }, [
        stringValue,
        shouldFormatNumber,
        formatNumber,
        numberOnly,
        shouldDisplayZero,
        intelNumber,
        isFocused,
      ]);

      useEffect(() => {
        if (disabled || disabledInput) {
          if (isFocused) setIsFocused(false);
          return;
        }

        if (!inputRef.current || typeof document === 'undefined') return;

        const hasFocus = document.activeElement === inputRef.current;
        if (hasFocus !== isFocused) {
          setIsFocused(hasFocus);
        }
      }, [disabled, disabledInput, inputRef, isFocused, value]);

      const handleFocus = useCallback(
        (event: FocusEvent<HTMLInputElement, Element>) => {
          setIsFocused(true);
          onFocus?.(event);
        },
        [onFocus, setIsFocused],
      );

      const handleBlur = useCallback(
        (event: FocusEvent<HTMLInputElement, Element>) => {
          setIsFocused(false);
          onBlur?.(event);
        },
        [setIsFocused, onBlur],
      );

      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
          target: { value },
        } = e;
        const selectionStart = e.target.selectionStart ?? value.length;
        const selectionEnd = e.target.selectionEnd ?? value.length;

        // The display effect inserts thousands separators for `shouldFormatNumber` AND for plain
        // `numberOnly` inputs (`intelNumber` defaults on), so the change event's value carries
        // commas in both cases. Validating against a comma-less charset here is what froze a
        // numberOnly field solid — backspace included — the moment its value reached 1,000.
        const isDisplayFormatted = shouldFormatNumber || Boolean(numberOnly && intelNumber);

        const regex = new RegExp(`^\\d*\\.?\\d{0,${maxDecimalPlaces}}$`);
        const numberOnlyRegexp = isDisplayFormatted
          ? maxDecimalPlaces
            ? new RegExp(`[^0-9,\\.]`)
            : new RegExp(`[^0-9,]`)
          : maxDecimalPlaces
            ? new RegExp(`[^0-9\\.]`)
            : new RegExp(`[^0-9]`);
        const sanitizedValue = isDisplayFormatted ? value.replace(/,/g, '') : value;

        if (
          (numberOnly && numberOnlyRegexp.test(value)) ||
          (maxDecimalPlaces && !regex?.test(sanitizedValue))
        ) {
          e.preventDefault();
          return;
        }

        const parsedValue = sanitizedValue;
        const postTransformedValue = regexp?.(parsedValue) || parsedValue;
        const nextValue = isDisplayFormatted
          ? formatNumber(postTransformedValue)
          : postTransformedValue;

        if (isDisplayFormatted && inputRef.current && typeof document !== 'undefined') {
          selectionRef.current = {
            start: mapCaretPosition(value, nextValue, selectionStart),
            end: mapCaretPosition(value, nextValue, selectionEnd),
          };
        }

        setInputValue(nextValue);
        onChange(postTransformedValue);

        if (isDisplayFormatted && selectionRef.current && inputRef.current) {
          const { start, end } = selectionRef.current;
          requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(start, end);
          });
        }
      };

      const handleClick = useCallback(() => {
        inputRef.current?.focus();
      }, [inputRef]);

      const hasValue = Boolean(isFocused || value);

      return (
        <div
          className={twMerge(
            'flex cursor-text flex-col gap-3 disabled:cursor-default',
            wrapperClassName,
          )}
          onClick={handleClick}
        >
          <div
            className={twMerge(
              `relative flex h-14 items-center gap-2.5 rounded-t bg-gray-50 px-2.5 ${inputStatusToWrapperClassNamesMap[customStatus ?? status]} ${disabled && 'cursor-default !border-transparent'}`,
              inputClassName,
            )}
          >
            {iconBeforeInput && (
              <div
                className={twMerge(onIconClick ? 'cursor-pointer' : '', iconBeforeInputClassName)}
                onClick={onIconBeforeInputClick}
              >
                {iconBeforeInput}
              </div>
            )}

            {placeholder && (
              <div
                className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 font-inter text-sm font-[450] transition-all duration-75 ${inputStatusToPlaceholderClassNamesMap[status]} ${hasValue ? '!top-[8px] !translate-y-0 !text-xs' : ''} ${placeholderClassName}`}
              >
                {placeholder}
              </div>
            )}
            <div className="flex h-full w-full flex-col justify-end pb-2 font-inter text-sm font-[450] text-gray-700">
              {!!value && currencySign && !!currency?.symbol && (
                <span className="translate-y-full">{currency.symbol}</span>
              )}

              <input
                className={twMerge(
                  'z-1 w-full truncate !bg-transparent pr-3 !outline-none',
                  className,
                  !!value && currencySign && 'pl-3',
                  !!value &&
                    currencySign &&
                    currency?.symbol &&
                    currency?.symbol?.length > 1 &&
                    'pl-8',
                )}
                disabled={disabledInput || disabled}
                onBlur={handleBlur}
                onChange={handleChange}
                onFocus={handleFocus}
                ref={inputRef}
                type={type}
                value={inputValue ?? ''}
                {...props}
              />
              {!!value && signAfterValue && (
                <span
                  className="absolute"
                  style={{
                    left: `${value.length * 9 + 10}px`,
                  }}
                >
                  {value.length * 9 + 10 > 200 ? '' : signAfterValue}
                </span>
              )}
            </div>
            {icon && (
              <div className={`${onIconClick ? 'cursor-pointer' : ''}`} onClick={onIconClick}>
                {icon}
              </div>
            )}
          </div>
          {errorMessage && isShownErrorMessage && (
            <div className="flex items-center gap-1">
              <InfoCircle />
              <div className="whitespace-pre-line text-left font-inter text-xs font-[450] text-fireside-600">
                {errorMessage}
              </div>
            </div>
          )}
        </div>
      );
    },
  ),
);

Input.displayName = 'Input';

export default Input;
